// @flow

import React, { type Node, useCallback } from 'react';
import styled from 'styled-components';

import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { getSafeTefPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';

import { RadioButton, Separator } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { getImageSourceFromCode } from './images';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';

export function BankList(): Node {
  const incomingPayment = useOpenedIncomingPayment();
  const [selectedBank, setSelectedBank] = useUserInputValue('selectedTefBank');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedTefBank');
  const submitting = useSubmitting();
  const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const bankList = getSafeTefPaymentOption(paymentOptions);
  const inputId = useUniqueId();
  const onChange = useCallback(e => setSelectedBank(e.target.value), [setSelectedBank]);

  return (
    <div data-test-name="payment-type-code">
      {bankList.map(({ value, name }) => (
        <div key={value}>
          <Label htmlFor={`${inputId}-${value}`}>
            <RadioButton
              id={`${inputId}-${value}`}
              name="payment_type_code"
              value={value}
              checked={value === selectedBank}
              onChange={onChange}
              disabled={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-test-value={value}
            />

            <Separator direction="h" size={4} />
            <BankIcon src={getImageSourceFromCode(value)} alt={value} />
            <Separator direction="h" size={4} />

            <span>{name}</span>
          </Label>
        </div>
      ))}

      {formHasBeenSent && validationError && <ValidationMessage validationError={validationError} />}
    </div>
  );
}

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const BankIcon = styled.img``;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>Selecione o seu banco</ErrorLabel>;

    case 'server-error':
      return <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>;

    default:
      return <ErrorLabel>Erro na seleção do banco</ErrorLabel>;
  }
}
